import { useRef, useState } from "react";
import { ArrowsOut } from "@phosphor-icons/react";

// Two-finger pinch + drag + double-tap zoom for phone drawing previews.
export const PinchZoom = ({ children, testid = "pinch-zoom", maxScale = 5 }) => {
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  const ptrs = useRef(new Map());
  const start = useRef(null);
  const lastTap = useRef(0);

  const center = () => {
    const pts = [...ptrs.current.values()];
    return pts.length === 2
      ? { d: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), cx: (pts[0].x + pts[1].x) / 2, cy: (pts[0].y + pts[1].y) / 2 }
      : { d: 0, cx: pts[0]?.x ?? 0, cy: pts[0]?.y ?? 0 };
  };
  const down = (e) => {
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    start.current = { ...center(), t };
    if (ptrs.current.size === 1) {
      const now = Date.now();
      if (now - lastTap.current < 300) setT((c) => (c.s > 1 ? { s: 1, x: 0, y: 0 } : { s: 2.5, x: 0, y: 0 }));
      lastTap.current = now;
    }
  };
  const move = (e) => {
    if (!ptrs.current.has(e.pointerId) || !start.current) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const c = center();
    const st = start.current;
    if (ptrs.current.size === 2 && st.d) {
      const s = Math.min(maxScale, Math.max(1, st.t.s * (c.d / st.d)));
      setT({ s, x: st.t.x + (c.cx - st.cx), y: st.t.y + (c.cy - st.cy) });
    } else if (ptrs.current.size === 1 && st.t.s > 1) {
      e.preventDefault();
      setT({ s: st.t.s, x: st.t.x + (c.cx - st.cx), y: st.t.y + (c.cy - st.cy) });
    }
  };
  const up = (e) => {
    ptrs.current.delete(e.pointerId);
    start.current = ptrs.current.size ? { ...center(), t } : null;
    if (ptrs.current.size === 0) setT((c) => (c.s <= 1.02 ? { s: 1, x: 0, y: 0 } : c));
  };

  return (
    <div className="relative">
      <div
        data-testid={testid}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onPointerLeave={up}
        className="overflow-hidden rounded-xl border border-zinc-800 bg-white p-2"
        style={{ touchAction: t.s > 1 ? "none" : "pan-y pinch-zoom" }}
      >
        <div style={{ transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})`, transformOrigin: "center", transition: ptrs.current.size ? "none" : "transform 120ms ease-out" }}>
          {children}
        </div>
      </div>
      {t.s > 1 ? (
        <button type="button" data-testid={`${testid}-reset`} onClick={() => setT({ s: 1, x: 0, y: 0 })} className="absolute right-2 top-2 rounded-full bg-zinc-900/90 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-zinc-100">
          {t.s.toFixed(1)}× · reset
        </button>
      ) : (
        <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-zinc-900/70 px-2 py-1 text-[10px] uppercase tracking-widest text-zinc-300">
          <ArrowsOut size={11} weight="bold" /> pinch
        </span>
      )}
    </div>
  );
};
